import {
  Autocomplete,
  Box,
  Divider,
  Drawer,
  Paper,
  TextField,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { Controller, useForm, UseFormReturn } from "react-hook-form";
import { useDependencyInjection } from "../../config/di/dependency_injection_context";
import { usePageLoader } from "../contexts/page_content_context";
import { processError } from "../utils/base_error";
import { usePageStartBootstrap } from "../hooks/use_page_start_bootstrap";
import { NomeAuditorDropDown } from "../domain/models/nome_auditor_dropdown";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormPhoneMaskedInput } from "./masked_inputs/react_forms/form_phone_masked_input";
import {
  AuditoriaAauditoresForm,
  AuditoriaAauditoresSchema,
} from "../../features/auditoria/presentation/schemas/Auditoria_auditores/auditoria_auditores";

import PrimaryButton from "./primary_button";
import DefaultFormInput from "./default_form_input";
import DynamicIcon from "./dynamic_icon";

//DropDownNomeAuditor

type Props<F extends {}> = {
  name: keyof F;
  label?: string;
  newButtonLabel?: string;
  required?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  reactHookForm: UseFormReturn<F>;
};

export const DropDownNomeAuditor = <F extends {}>({
  name,
  label,
  newButtonLabel,
  required,
  readOnly,
  placeholder,
  reactHookForm,
}: Props<F>) => {
  const [openModal, setOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const { pushErrorNotification, pushInfoNotification } = usePageLoader();

  const [nomeAuditorDropDown, setNomeAuditorDropDown] = useState<
    NomeAuditorDropDown[]
  >([]);

  const {
    commons: { getNomeAuditorDropDownUseCase },
    auditor: { createAuditoresUseCase },
  } = useDependencyInjection();

  const hookForm = useForm<AuditoriaAauditoresForm>({
    reValidateMode: "onSubmit",
    mode: "onSubmit",
    disabled: saving,
    resolver: zodResolver(AuditoriaAauditoresSchema),
  });

  const loadFornecedorDropDown = useCallback(async () => {
    try {
      const result =
        await getNomeAuditorDropDownUseCase.getNomeAuditorDropDown();
      setNomeAuditorDropDown(result);
    } catch (e: unknown) {
      throw new Error("Falha ao buscar nomes do auditores!");
    }
  }, [getNomeAuditorDropDownUseCase]);

  const handleAuditorlSubmit = useCallback(
    async (form: AuditoriaAauditoresForm) => {
      setSaving(true);
      try {
        const id = await createAuditoresUseCase.createAuditores(
          form.Nome ?? "",
          form.TelFixo ?? "",
          form.TelCelular ?? "",
          form.Email ?? "",
          form.OutrasInformacoes ?? "",
          form.Cidade ?? "",
          null,
          null,
          null,
          null,
          null,
          0
        );
        pushInfoNotification("Sucesso", "Auditor criado com sucesso!", {
          isAutoFinish: true,
        });

        reactHookForm.setValue(name as any, id as any);
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao criar Auditor!"),
        );
      } finally {
        setSaving(false);
        loadFornecedorDropDown();
        setOpenModal(false);
      }
    },
    [
      createAuditoresUseCase,
      loadFornecedorDropDown,
      name,
      pushErrorNotification,
      pushInfoNotification,
      reactHookForm,
    ],
  );

  const CustomPaper = useMemo(
    () =>
      ({ children, ...props }: any) => (
        <Paper {...props}>
          <div>
            {children}
            <Divider />
            <div>
              <div
                className="py-2 px-4 cursor-pointer rounded-[4px] font-semibold flex items-center gap-2 text-sm text-primary-green hover:bg-hover-light-gray"
                onMouseDown={() => setOpenModal(true)}
                role="button"
                tabIndex={0}
              >
                <DynamicIcon
                  nameIcon="Add"
                  size={20}
                  className="text-primary-green"
                />
                <span>{newButtonLabel ?? "Novo"}</span>
              </div>
            </div>
          </div>
        </Paper>
      ),
    [newButtonLabel],
  );

  usePageStartBootstrap(async () => {
    loadFornecedorDropDown();
  });

  return (
    <>
      {/*DROPDOWN */}

      <Controller
        name={name as any}
        control={reactHookForm.control}
        render={({ field, formState }) => {
          let helperText: string | null = null;

          if (formState.errors[name as keyof typeof formState.errors]) {
            const fieldError = formState.errors[name] as any;
            if (fieldError?.message && typeof fieldError.message === "string") {
              helperText = fieldError.message;
            }
          }

          return (
            <Autocomplete
              fullWidth={true}
              size="small"
              disablePortal
              options={nomeAuditorDropDown}
              getOptionKey={(opt) => opt.idAuditor}
              getOptionLabel={(opt) => opt.nomeAuditor}
              isOptionEqualToValue={(opt, val) =>
                opt.idAuditor === val.idAuditor
              }
              sx={{ minHeight: 45 }}
              slots={{
                paper: CustomPaper,
              }}
              readOnly={readOnly}
              disabled={formState.disabled}
              value={
                nomeAuditorDropDown.find((x) => x.idAuditor === field.value) ??
                null
              }
              onChange={(_, newValue) =>
                field.onChange(newValue ? newValue.idAuditor : undefined)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={label}
                  error={helperText !== null}
                  helperText={helperText}
                  placeholder={placeholder}
                  required={required}
                />
              )}
            />
          );
        }}
      />
      <Drawer
        anchor="right"
        open={openModal}
        onClose={() => setOpenModal(false)}
        style={{ zIndex: 1300 }}
        PaperProps={{
          style: {
            width: 500,
            padding: 20,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          },
        }}
      >
        <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
          <h2 className="text-lg font-semibold mb-4">Adicionar Auditor</h2>
          <div className="flex flex-col items-start w-[25rem] gap-4">
            <DefaultFormInput
              name="Nome"
              label="Nome"
              required={true}
              reactHookForm={hookForm}
            />
            <div className="flex flex-row gap-4 w-full">
              <div className="flex-1">
                <FormPhoneMaskedInput
                  name="TelCelular"
                  type="movel"
                  label="Tel. Celular"
                  reactHookForm={hookForm}
                />
              </div>
              <div className="flex-1">
                <FormPhoneMaskedInput
                  name="TelFixo"
                  type="movel"
                  label="Tel. Fixo"
                  reactHookForm={hookForm}
                />
              </div>
            </div>
            <DefaultFormInput
              name="Email"
              label="Email"
              reactHookForm={hookForm}
            />
          </div>
          <DefaultFormInput
            name="Cidade"
            label="Cidade"
            reactHookForm={hookForm}
          />
          <DefaultFormInput
            name="OutrasInformacoes"
            label="Outras Informacoes"
            rows={4}
            multiline={true}
            reactHookForm={hookForm}
          />
        </Box>

        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            width: "100%",
            background: "white",
            padding: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold"
            onClick={() => setOpenModal(false)}
          >
            <h1 className="text-sm text-black dark:text-white">Cancelar</h1>
          </Box>

          <Box width="120px">
            {saving ? (
              <PrimaryButton buttonColor="primary-green">
                Salvando...
              </PrimaryButton>
            ) : (
              <PrimaryButton
                buttonColor="primary-green"
                onClick={hookForm.handleSubmit(handleAuditorlSubmit)}
              >
                Salvar
              </PrimaryButton>
            )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};
